import { DateFormat } from '@/constants/DateFormat';
import type { IPortfolioTree, IPortfolioTreeStrategy, IPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import { isPortfolioTreeStrategy, isPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import type { ComputedRef, Ref, WatchStopHandle } from 'vue';
import { computed, ref, watch } from 'vue';
import type { DateTime } from 'luxon';
import { createDate } from '@/utils/dateUtils';
import { useItemUnderAnalysisGlobalState } from './useItemUnderAnalysis';
import { useIsSystemLoading } from './useSystemLoading';
import { translateKnownStrategyProperty } from '@/utils/strategy';
import { preciseValue, units } from '@/filters';
import { displayNum } from '@/composables/useNumberUtilities';
import useFxConversion from './useFxConversion';
import { MetricConstants } from '@/constants/MetricConstants';
import useStrategyMask from '@/composables/useStrategyMask';
import { notNull } from '@/utils/notnull';
import type { CustomVaR } from '@/types/analytics/ConstituentRisk';
import {
  CustomVaRHorizon,
  CustomVaRScenarioTenor,
  dataComponents,
  dataHistoryComponents,
  FilterConstants,
  HistoryGroupBy,
  LEGEND_DATA_MAX,
  RiskTabs,
  UnitConstants,
  VarMetrics,
} from '@/types/analytics/ConstituentRisk';
import type { RiskDashboardComponentsConstants } from '@/types/IConstituentRisk';
import { useGetHistoricalTrackColor } from '@/utils/ConstituentRisk';
import { useIsPdf } from './usePdf';
import { useConstituentRiskAnalysisSteps } from './useAnalysisSteps';
import { useRiskPreferences } from './useRiskPreferences';
import type { IConstituentRiskMetricTrack } from '@/types/IConstituentRiskMetricTrack';
import type { StrategyLookupMap } from './useDiscoverStore';
import { useDiscoverStore } from './useDiscoverStore';
import { createGlobalState } from '@vueuse/shared';
import useUser from './useUser';
import type TranslateParams from '@/types/TranslateParams';
import { useStrategyFactsheet } from './queries/useStrategies';
import useStrategyReportingParameters from './useStrategyReportingParameters';
import { formatValueWithFieldConfig, resolveCurrencyForFieldConfig } from '@/utils/riskDashboardFieldFormatting';
import type { RiskDashboardFieldConfigsResponse } from '@/types/RiskDashboardFieldConfig';

/**
 * Optional formatting inputs for a cell.
 * Call sites pass only what they know; the rest falls back to default formatting.
 */
export type CellContentFormatting = {
  optionsUnit?: UnitConstants;
  fieldConfigs?: RiskDashboardFieldConfigsResponse;
  rowCurrency?: string | null;
  barraFormattedColumnKeys?: ReadonlySet<string>;
};

export function useConstituentRiskUtilities(): {
  paddingHeight: string;
  shortDivHeight: string;
  tallDivHeight: string;
  riskDashboardComponents: ComputedRef<RiskDashboardComponentsConstants[]>;
  unitTypeTranslation: { [key: string]: TranslateParams };
  checkToUseOverlay: (activeCodes: string[], tree: IPortfolioTree | null) => boolean;
  convertToConstituentDate: (params: {
    targetDate: DateTime;
    availableDates: string[];
    isRolling?: boolean;
    goForward?: boolean;
  }) => DateTime | null;
  getShouldShowOverlay: (
    dataset: Ref<Record<string, unknown[]> | unknown[] | null>,
    isDataLoading: Ref<boolean>,
  ) => ComputedRef<boolean>;
  getCellContent: (
    metricVal: number | undefined | null,
    staticVal: string | number | undefined | null,
    column: string,
    formatting?: CellContentFormatting,
  ) => string;
  getProcessedLegendData: (
    paramPreferencesMetric: MetricConstants,
    legendData: {
      displayName: string;
      name: string;
      color: string;
    }[],
  ) => {
    displayName: string;
    name: string;
    color: string;
  }[];
  getMetricsRange: (metrics: Ref<Record<string, string | number | undefined>[]>) => {
    hasPositive: ComputedRef<boolean>;
    hasNegative: ComputedRef<boolean>;
  };
  getStrategyNameFromCode: (key: string, allStrategiesByCode: StrategyLookupMap, idx?: number) => string;
  getVarMetricsOptions: () => ComputedRef<{ text: string; value: VarMetrics }[]>;
  getTrackSegment: () => FilterConstants | HistoryGroupBy;
  getVarHistoryLegendData: (data: IConstituentRiskMetricTrack) => {
    displayName: string;
    name: string;
    color: string;
  }[];
  getSanitizedLegendData: (
    data: {
      displayName: string;
      name: string;
      color: string;
    }[],
  ) => {
    displayName: string;
    name: string;
    color: string;
  }[];
  getCustomVaRMetric: (customVaR: CustomVaR) => string;
  getHorizonDisplayName: (days: number) => string;
  getHorizonDays: (horizon: CustomVaRHorizon) => number;
} {
  const { user } = useUser();
  const { itemUnderAnalysis } = useItemUnderAnalysisGlobalState();
  const { toCurrency } = useFxConversion();
  const isSystemLoading = useIsSystemLoading();
  const { getStrategyMask, getMaskedName, getPropertyFromPreference: getMaskPreference } = useStrategyMask();
  const { activeAnalysisSubstep } = useConstituentRiskAnalysisSteps();
  const constituentAnalysisDate = useConstituentAnalysisDate();
  const isHistoryMode = useHistoryMode();

  const paddingHeight = '1.5rem';
  const shortDivHeight = '250px';
  const tallDivHeight = `calc(${paddingHeight} + calc(${shortDivHeight} * 2))`;

  const riskDashboardComponents = computed((): RiskDashboardComponentsConstants[] => {
    if (!activeAnalysisSubstep.value) return [];

    const arrayToUse = isHistoryMode.value ? dataHistoryComponents : dataComponents;
    return arrayToUse[activeAnalysisSubstep.value.path as RiskTabs] ?? [];
  });

  /**
   * Looks through the entire portfolio to see if at least one strategy is active.
   * @param components Subportfolio components
   */
  const checkForActive = ({
    components,
    analysisCodes,
  }: {
    components: (IPortfolioTreeSubportfolio | IPortfolioTreeStrategy)[];
    analysisCodes: string[];
  }): boolean | undefined => {
    for (const child of components) {
      if (isPortfolioTreeSubportfolio(child)) {
        if (
          checkForActive({
            components: child.components,
            analysisCodes,
          })
        )
          return true;
      }

      if (isPortfolioTreeStrategy(child)) {
        const codeToUse = child.strategy?.code ?? child.reference;
        if (codeToUse && analysisCodes.includes(codeToUse)) return true;
      }
    }
  };

  /**
   * Checks that the portfolio has active strategies and includes the necessary strategies to show the data needed for that tab.
   * The Equity tab requires at least one Equity strategy.
   */
  const checkToUseOverlay = (activeCodes: string[], tree: IPortfolioTree | null): boolean => {
    if (!tree) return false;

    if (
      checkForActive({
        components: tree.portfolioTree.components,
        analysisCodes: activeCodes,
      })
    )
      return false;

    return true;
  };

  /**
   * goForward - For YTD, QTD, and MTD, we want to ensure that the datapoint used is the one that is BEFORE
   * the current period. In other words, if we select YTD, we are targeting a date of 31 Dec. If
   * 31 Dec is not a business day, or we do not have data for that day, we don't want 1 Jan, we want
   * the next available date that is in the past (e.g., 30 Dec).
   * Replaces the selected date by the nearest valid constituent date.
   */
  const convertToConstituentDate = ({
    targetDate,
    availableDates,
    isRolling,
    goForward,
  }: {
    targetDate: DateTime;
    availableDates: string[];
    isRolling?: boolean;
    goForward?: boolean;
  }): DateTime | null => {
    if (!availableDates.length) return null;

    if (availableDates.includes(targetDate.toFormat(DateFormat.YYYY_MM_DD))) return targetDate;

    let nearestDate = createDate(availableDates[0]);
    for (const possibleDateString of availableDates) {
      const possibleDate = createDate(possibleDateString);
      const numDaysFromNearestDateCandidate = Math.abs(nearestDate.diff(targetDate, 'days').days);
      const numDaysFromPossibleDate = Math.abs(possibleDate.diff(targetDate, 'days').days);

      const directionIsCorrect = (goForward && targetDate < possibleDate) || (!goForward && targetDate > possibleDate);

      // If the new available targetDate is smaller than the previous nearest targetDate, nearest targetDate gets replaced
      if (numDaysFromPossibleDate < numDaysFromNearestDateCandidate && directionIsCorrect) {
        // Prevent the rolling targetDate to be the same as the position targetDate.
        if (
          isRolling &&
          constituentAnalysisDate.value &&
          possibleDate.toMillis() === createDate(constituentAnalysisDate.value).toMillis()
        )
          continue;
        nearestDate = createDate(possibleDate);
      }
    }
    return nearestDate;
  };

  /**
   * Should the overlay be shown
   * @param dataset
   */
  const getShouldShowOverlay = (
    dataset: Ref<Record<string, unknown[]> | unknown[] | null>,
    isDataLoading: Ref<boolean>,
  ): ComputedRef<boolean> => {
    const analysisCodesForRisk = useAnalysisCodesForRisk();
    const { draft, shouldTreatRootItemAsPortfolioOnConstituentTools } = useItemUnderAnalysisGlobalState();

    const isPdf = useIsPdf();

    return computed((): boolean => {
      // The overlay should not be shown until the system has finished loading
      if (isSystemLoading.value || isDataLoading.value) return false;

      // If the dataset is null, we should show the overlay
      if (!dataset.value) return true;

      // Once the dataset is defined we can check if it is an empty array or empty object
      if (!Object.keys(dataset.value).length) return true;

      // on PDF we want to use ALL codes. but on the platform, we only use the ones we have pinned
      const codesToUse = isPdf.value
        ? (draft.value?.portfolioTree.flattenedAllCodesForRisk ?? [])
        : analysisCodesForRisk.value;

      if (shouldTreatRootItemAsPortfolioOnConstituentTools.value === false) return false;

      return checkToUseOverlay(codesToUse, draft.value);
    });
  };

  /**
   * Formats the cell appropriately
   */
  const getCellContent = (
    metricVal: number | undefined | null,
    staticVal: string | number | undefined | null,
    column: string,
    formatting?: CellContentFormatting,
  ): string => {
    const { optionsUnit, fieldConfigs, rowCurrency, barraFormattedColumnKeys = new Set<string>() } = formatting ?? {};
    const cellValue = metricVal ?? staticVal;
    const fieldConfig = fieldConfigs?.[column];

    // Formats the number cells to include the correct unit and number styling
    if (typeof cellValue === 'number') {
      if (fieldConfig) {
        const currency = resolveCurrencyForFieldConfig(fieldConfig, rowCurrency, toCurrency.value);
        const formattedValue = formatValueWithFieldConfig(cellValue, fieldConfig, {
          currency,
          column,
          displayUnit: optionsUnit,
          isStaticValue: staticVal != null && metricVal == null,
        });

        if (formattedValue) {
          return formattedValue;
        }
      }

      // Barra exposures are unit exposures, not percentages: no scaling and no percent sign.
      if (
        metricVal != undefined &&
        (optionsUnit === UnitConstants.PERCENT_VALUE || optionsUnit === UnitConstants.PERCENT_SELECTION) &&
        barraFormattedColumnKeys.has(column)
      ) {
        return displayNum({ value: cellValue, metric: column, numDec: 3, showSuffix: false });
      }

      // For static cells we do not want any units
      const unit = metricVal != undefined && !!optionsUnit ? optionsUnit : '';

      // By default, we should have 2 decimal places to preserve existing behavior

      // Currently if we just have a metric value, we want to show 3 decimal places that's not a price column
      if (optionsUnit === undefined && metricVal != undefined) {
        return cellValue.toFixed(3);
      }

      return units(cellValue, unit, { currency: toCurrency.value, numDecimalPoints: 2 });
    }

    // Formats the string cells to include the correct translation
    if (typeof cellValue === 'string') {
      // Some cells may have aggregated values, those need to be translated individually
      const delimiter = ' + ';
      return (
        cellValue
          .split(delimiter)
          // For each value, translate it given its current column name
          .map((property) => translateKnownStrategyProperty(column, property) ?? property)
          .join(delimiter)
      );
    }

    return '-';
  };

  /**
   * For convexity graphs,
   * if we have only one series showing,
   * we display the series' name in the legend instead of its value (e.g. Vol+0%).
   */
  const getProcessedLegendData = (
    paramPreferencesMetric: MetricConstants,
    legendData: {
      displayName: string;
      name: string;
      color: string;
    }[],
  ): {
    displayName: string;
    name: string;
    color: string;
  }[] => {
    if (
      (paramPreferencesMetric === MetricConstants.CONVEXITY ||
        paramPreferencesMetric === MetricConstants.CONVEXITY_NORM ||
        paramPreferencesMetric === MetricConstants.CONVEXITY_BS) &&
      legendData.length === 1
    ) {
      return legendData
        .map((e) => {
          if (!itemUnderAnalysis.value) return;

          return { ...e, name: e.name, displayName: getStrategyMask(itemUnderAnalysis.value) };
        })
        .filter(notNull);
    }

    return legendData;
  };

  const getMetricsRange = (
    metrics: Ref<Record<string, string | number | undefined>[]>,
  ): {
    hasPositive: ComputedRef<boolean>;
    hasNegative: ComputedRef<boolean>;
  } => {
    const hasPositive = computed(
      (): boolean => !!Object.values(metrics.value).find((value) => !!(typeof value === 'number' && value > 0)),
    );
    const hasNegative = computed(
      (): boolean => !!Object.values(metrics.value).find((value) => !!(typeof value === 'number' && value < 0)),
    );

    return {
      hasPositive,
      hasNegative,
    };
  };

  /**
   * Used to show the strategy name (short name/long name/code/ticker | short name) from a code.
   * @param key Strategy Code
   */
  const getStrategyNameFromCode = (key: string, allStrategiesByCode: StrategyLookupMap, idx?: number): string => {
    const strategy = allStrategiesByCode.get(key);
    let strategyName = strategy
      ? getStrategyMask(strategy)
      : (getMaskedName(key, getMaskPreference(user.value?.displayNamePreference)) ?? key);

    // Necessary since some portfolios may only contain a strategy that is the same name as the portfolioName
    // In that case we add a space to the strategy name, so that amcharts does not get confused
    if (itemUnderAnalysis.value && strategyName === getStrategyMask(itemUnderAnalysis.value) && idx !== 0) {
      strategyName = `${strategyName} `;
    }

    return strategyName;
  };

  const getVarMetricsOptions = (): ComputedRef<{ text: string; value: VarMetrics }[]> => {
    const { preferences } = useRiskPreferences();

    return computed((): { text: string; value: VarMetrics }[] => {
      const customVarMetric = getCustomVaRMetric(preferences.value.options.customVaR);

      return Object.values(VarMetrics).map((m) => {
        if (m === VarMetrics.CUSTOM_VAR) return { text: customVarMetric, value: VarMetrics.CUSTOM_VAR };

        return { text: `H-${m}`, value: m };
      });
    });
  };

  /**
   * Based on the tab we want to show a historical track with a different segment,
   */
  const getTrackSegment = (): FilterConstants | HistoryGroupBy => {
    const { activeAnalysisSubstep } = useConstituentRiskAnalysisSteps();
    const { preferences } = useRiskPreferences();

    switch (activeAnalysisSubstep.value?.path) {
      case RiskTabs.VAR:
        return preferences.value.var.historyCategory;
      case RiskTabs.STRESS_TEST:
        return preferences.value['stress-test'].historyCategory;
      case RiskTabs.EQUITY:
        return preferences.value.equity.historyCategory;
      case RiskTabs.INTEREST_RATE:
        return preferences.value['interest-rate'].historyCategory;
      case RiskTabs.CREDIT:
        return preferences.value.credit.historyCategory;
      case RiskTabs.FX:
        return preferences.value.fx.historyCategory;
      case RiskTabs.COMMODITY:
        // Group By Commodity is a display only option, we need to use the underlying
        return preferences.value.commodity.historyCategory === HistoryGroupBy.COMMODITY
          ? HistoryGroupBy.UNDERLYING
          : preferences.value.commodity.historyCategory;
      default:
        return FilterConstants.STRATEGY;
    }
  };

  const getVarHistoryLegendData = (
    data: IConstituentRiskMetricTrack,
  ): {
    displayName: string;
    name: string;
    color: string;
  }[] => {
    const isPdf = useIsPdf();
    const getColor = useGetHistoricalTrackColor();
    const trackSegment = computed(() => getTrackSegment());

    const { allStrategiesByCode } = useDiscoverStore();

    if (
      isPdf.value ||
      (!isPdf.value &&
        (trackSegment.value === FilterConstants.CURRENCY || trackSegment.value === HistoryGroupBy.UNDERLYING))
    ) {
      return Object.keys(data)
        .map((strategyCode, idx) => {
          return {
            displayName: getStrategyNameFromCode(strategyCode, allStrategiesByCode.value),
            name: strategyCode,
            color: getColor(strategyCode, idx),
          };
        })
        .slice(0, LEGEND_DATA_MAX);
    }

    return [];
  };

  const getSanitizedLegendData = (
    data: {
      displayName: string;
      name: string;
      color: string;
    }[],
  ): {
    displayName: string;
    name: string;
    color: string;
  }[] => {
    return Array.from(new Set(data))
      .slice(0, LEGEND_DATA_MAX)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  };

  const unitTypeTranslation: { [key: string]: TranslateParams } = {
    [UnitConstants.CURRENCY]: { path: 'GLOBAL.DATABASE_TRANSLATOR.IN_CURRENCY' },
    [UnitConstants.PERCENT_VALUE]: { path: 'GLOBAL.DATABASE_TRANSLATOR.IN_PERCENT_OF_NOTIONAL' },
    [UnitConstants.PERCENT_SELECTION]: { path: 'GLOBAL.DATABASE_TRANSLATOR.IN_PERCENT_OF_SELECTION' },
  };

  const getScenarioTenorDisplayName = (scenarioTenor: CustomVaRScenarioTenor): string => {
    if (scenarioTenor === CustomVaRScenarioTenor.FIVE_DAY_OVERLAP) {
      return '5D';
    }

    if (scenarioTenor === CustomVaRScenarioTenor.FIVE_DAY_FRIDAYS) {
      return '5DF';
    }

    return '1D';
  };

  const getHorizonDisplayName = (days: number): string => {
    const DAYS_PER_WEEK = 5;
    const DAYS_PER_MONTH = 20;

    if (days % DAYS_PER_MONTH === 0) {
      const months = days / DAYS_PER_MONTH;
      return `${months}M`;
    }

    if (days % DAYS_PER_WEEK === 0) {
      const weeks = days / DAYS_PER_WEEK;
      return `${weeks}W`;
    }

    return `${days}D`;
  };

  const getHorizonDays = (horizon: CustomVaRHorizon): number => {
    if (horizon === CustomVaRHorizon.ONE_DAY) {
      return 1;
    }

    if (horizon === CustomVaRHorizon.ONE_WEEK) {
      return 5;
    }

    return 20;
  };

  const getCustomVaRMetric = ({
    type,
    scenarioTenor,
    decay,
    lookbackPeriod,
    stressedPeriod,
    confidenceLevel,
    horizon,
  }: CustomVaR): string => {
    const lookbackPeriodDisplayName = lookbackPeriod ?? '';
    const stressedPeriodDisplayName = stressedPeriod ? '1Y' : '';
    const additionSymbol = lookbackPeriod && stressedPeriod ? '+' : '';
    const scenarioTenorDisplayName = getScenarioTenorDisplayName(scenarioTenor);
    const decayDisplayName = decay !== 1 ? ` ${Number(decay.toFixed(3)).toString()}` : '';
    const horizonDisplayName = getHorizonDisplayName(horizon);
    const confidenceLevelDisplayName = confidenceLevel != null ? preciseValue(confidenceLevel * 100) : '';

    const fieldName = `${type} [${scenarioTenorDisplayName} ${lookbackPeriodDisplayName}${additionSymbol}${stressedPeriodDisplayName}${decayDisplayName}] ${confidenceLevelDisplayName}% ${horizonDisplayName}`;

    return fieldName;
  };

  return {
    paddingHeight,
    shortDivHeight,
    tallDivHeight,
    riskDashboardComponents,
    unitTypeTranslation,
    checkToUseOverlay,
    convertToConstituentDate,
    getShouldShowOverlay,
    getCellContent,
    getProcessedLegendData,
    getMetricsRange,
    getStrategyNameFromCode,
    getVarMetricsOptions,
    getTrackSegment,
    getVarHistoryLegendData,
    getSanitizedLegendData,
    getCustomVaRMetric,
    getHorizonDisplayName,
    getHorizonDays,
  };
}

export function resolveConstituentTableColumnUnit({
  columnKey,
  barraFormattedColumnKeys,
  isPositionMode,
  positionDisplayUnit,
  riskDisplayUnit,
}: {
  columnKey: string;
  barraFormattedColumnKeys: ReadonlySet<string>;
  isPositionMode: boolean;
  positionDisplayUnit: UnitConstants;
  riskDisplayUnit: UnitConstants;
}): UnitConstants | undefined {
  if (barraFormattedColumnKeys.has(columnKey)) {
    if (isPositionMode) {
      return positionDisplayUnit;
    }

    return undefined;
  }

  return isPositionMode ? positionDisplayUnit : riskDisplayUnit;
}

/**
 * Codes passed to the risk APIs
 */
export const useAnalysisCodesForRisk = createGlobalState((): Ref<string[]> => {
  return ref<string[]>([]);
});

/**
 * Serves as the analysis date for Constituent Risk and Constituent Position pages
 *
 * The API uses this date in two ways:
 * 1. It selects the appropriate Position Date that is equal to or before this date
 * 2. It calculates the risk metrics as of this date
 */
export const useConstituentAnalysisDate = createGlobalState((): Ref<string | null> => {
  return ref<string | null>(null);
});

/**
 * Rolling date for the constituent risk page
 */
export const useRollingDate = createGlobalState((): Ref<string | undefined> => {
  return ref<string>();
});

/**
 * Toggle for history mode
 */
export const useHistoryMode = createGlobalState((): Ref<boolean | undefined> => {
  return ref<boolean>();
});

export const useResetStrategyReportingParametersOnRootItemChange = (
  indexUniqueIdentifier: ComputedRef<string | undefined>,
  shouldTreatRootItemAsPortfolioOnConstituentTools: Ref<boolean | null>,
): WatchStopHandle => {
  const strategy = useStrategyFactsheet(indexUniqueIdentifier, {
    enabled: computed(() => shouldTreatRootItemAsPortfolioOnConstituentTools.value === false),
  });
  const strategyData = computed(() => strategy.data.value?.strategy);
  const analysisCodesForRisk = useAnalysisCodesForRisk();
  const { toDefault: toDefaultStrategyParameters } = useStrategyReportingParameters();

  // it is important to set this so that the sidebar behaves as expected for the strategy
  // eslint-disable-next-line no-restricted-syntax
  return watch(
    [strategyData, shouldTreatRootItemAsPortfolioOnConstituentTools],
    ([newStrategyData, newShouldTreatRootItemAsPortfolioOnConstituentTools]) => {
      if (newStrategyData && newShouldTreatRootItemAsPortfolioOnConstituentTools === false) {
        // update analysisCodesForRisk with the new strategy data
        analysisCodesForRisk.value = [newStrategyData.code];

        // reset the reporting parameters for the new strategy
        toDefaultStrategyParameters();
      }
    },
    { immediate: true },
  );
};
